import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Formik } from "formik";
import styled from "styled-components";
import * as Yup from "yup";

import AlertBox from "components/global/AlertBox";
import Loader from "components/global/Loader";
import Logo from "components/global/Logo";
import Spacer from "components/global/Spacer";
import FormWrapper from "components/global/FormWrapper";
import FormGroup from "components/global/FormGroup";
import Button from "components/global/Button";

import { applyReferralCode, getTooltip, socialLogin } from "api";
import { showAlert } from "utils/utils";
import usePageTitle from "hooks/usePageTitle";

const Wrapper = styled.div`
  width: 100vw;
  height: 100vh;

  .inner {
    padding: 24px;
    width: min(480px, 100%);
    margin: auto;
  }

  .header {
    margin-bottom: 20vh;
    border-bottom: 1px solid #eaeaea;
  }

  .heading {
    color: var(--grey_1);
    width: 39.6rem;
  }

  .subHeading {
    color: var(--body_text);
    width: 58.3rem;
  }

  .verifyEmailImg {
    display: block;
    width: 14.8rem;
    margin: 0 auto;
  }

  @media screen and (max-width: 768px) {
    .header {
      margin-bottom: 10vh;
    }

    .heading {
      font-size: 24px;
      line-height: 32px;
      width: 28.6rem;
    }

    .subHeading {
      font-size: 18px;
      line-height: 24px;
      width: 100%;
    }
  }
`;

const buildRedirectUrl = (redirectPath, connectedTo, accessToken) => {
  if (!redirectPath || redirectPath === "null") {
    return "/dashboard";
  }

  const params = [];
  if (connectedTo) params.push(`connected_to=${connectedTo}`);
  if (accessToken) params.push(`access_token=${accessToken}`);

  if (params.length === 0) return redirectPath;

  const connector = redirectPath.includes("?") ? "&" : "?";
  return `${redirectPath}${connector}${params.join("&")}`;
};

const AuthRedirect = () => {
  usePageTitle("Social Login");

  const router = useHistory();
  const { search } = useLocation();
  const { provider } = useParams();

  const [loading, setLoading] = useState(true);
  const [loginResponse, setLoginResponse] = useState();
  const handleRedirect = useCallback(
    async (data) => {
      const { access_token, connected_to } = data ?? loginResponse;
      const redirectPath = sessionStorage.getItem("return_to");

      sessionStorage.removeItem("referralCode");

      router.replace(
        redirectPath && redirectPath !== "null"
          ? buildRedirectUrl(redirectPath, connected_to, access_token)
          : "/onboarding"
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [loginResponse]
  );

  const handleLogin = useCallback(async () => {
    try {
      const res = await socialLogin(provider, search);
      if (res?.data?.status === false) {
        showAlert({
          msg: "Something went wrong. Please try again",
          type: "danger",
        });
        router.replace("/sign-up");
      }

      await getTooltip();

      setLoginResponse(res?.data?.data);

      const referralCode = sessionStorage.getItem("referralCode");

      if (!referralCode || referralCode === "null") {
        await handleRedirect(res?.data?.data);
      } else {
        setLoading(false);
      }
    } catch (e) {
      console.log(e);
      showAlert({
        msg: "An error occurred. Try again later or contact support",
        type: "danger",
      });
      router.replace("/sign-up");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleRedirect]);

  const handleSubmitReferralCode = useCallback(
    async ({ code }) => {
      try {
        const res = await applyReferralCode(code);

        if (res?.data?.status === true) {
          handleRedirect();
        }
      } catch (e) {
        console.log(e);
        showAlert({
          msg:
            e.response?.data?.message ??
            "An error occurred. Please contact support if the issue persists.",
          type: "danger",
        });
      }
    },
    [handleRedirect]
  );

  useEffect(() => {
    handleLogin();
    // eslint-disable-next-line
  }, []);

  const validationSchema = Yup.object({
    code: Yup.string().required("Field required"),
  });

  return (
    <Wrapper>
      <AlertBox />
      <div className="flexColumn alignCenter justifyCenter fullWidth header">
        <Spacer y={2.4} />
        <Logo />
        <Spacer y={2.4} />
      </div>
      {!loading ? (
        <div className="flexColumn alignCenter inner">
          <h4 className="title">Use referral code</h4>
          <Spacer y={0.6} />
          <p className="description colorGrey6 textCenter">
            Claim the discount from your referral code. Use the chat button if
            you have any problems.
          </p>
          <Spacer y={2.4} />
          <Formik
            initialValues={{
              code: sessionStorage.getItem("referralCode") ?? "",
            }}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              setSubmitting(true);
              await handleSubmitReferralCode(values);
              setSubmitting(false);
            }}
          >
            {({ handleSubmit, isSubmitting, isValid, values }) => (
              <FormWrapper onSubmit={handleSubmit}>
                <FormGroup
                  fieldStyle="shortText"
                  label="Referral code"
                  name="code"
                  className="spanFull"
                />

                <Button
                  type="submit"
                  text={"Apply code"}
                  className="spanFull"
                  disabled={!isValid || !values.code || isSubmitting}
                  loading={isSubmitting}
                  fullWidth
                />
                <Button
                  type="button"
                  text={"Continue to my Dashboard"}
                  className="noBorder spanFull"
                  onClick={handleRedirect}
                  disabled={isSubmitting}
                  fullWidth
                />
              </FormWrapper>
            )}
          </Formik>
        </div>
      ) : (
        <div className="flexColumn alignCenter inner">
          <Spacer y={2.4} />
          {loading && (
            <>
              <Loader />
              <Spacer y={1.2} />
              <p className="body-2">Signing you in</p>
            </>
          )}
        </div>
      )}
    </Wrapper>
  );
};

export default AuthRedirect;
